import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Project Nav-Data Store (Story 120.10)
 *
 * Cross-MFE bridge for the **shell-hosted** project sidebar. The shell renders
 * the sidebar in its own React tree, so MFEs cannot pass it badge counts or the
 * project list via props/slots. Instead:
 *
 *  - the **projects MFE writes** the data it owns (epic/notes/report counts +
 *    the user's project list) here as it loads, and
 *  - the **shell-hosted sidebar reads** it.
 *
 * Other project MFEs (e.g. release-management) simply write nothing → the
 * sidebar renders without badges on their routes, which is correct (those
 * counts are meaningless there). This keeps the sidebar presentational and
 * avoids any projects↔release coupling or host-owned domain fetching.
 *
 * Redux DevTools enabled in development for debugging.
 */

/** Per-project badge counts shown in the Planning Studio / core nav. */
export interface ProjectNavCounts {
  epicCount?: number;
  notesCount?: number;
  reportCount?: number;
  /** Reports awaiting review — badges the Report Hub Overview entry; hidden at 0. */
  awaitingReviewCount?: number;
}

/** Minimal project shape the switcher needs (kept local to avoid type coupling). */
export interface ProjectNavSummary {
  id: string;
  name: string;
  /** Owning client/tenant — used to group the switcher list. */
  clientId?: string;
}

interface ProjectNavDataStore {
  /** Badge counts keyed by projectId. */
  countsByProject: Record<string, ProjectNavCounts>;
  /** The user's project list (for the switcher). Empty until a writer populates it. */
  projects: ProjectNavSummary[];

  // --- Writer actions (called by the data-owning MFE, e.g. projects) ---
  /** Merge counts for a project (partial — callers set whichever they own). */
  setCounts: (projectId: string, counts: Partial<ProjectNavCounts>) => void;
  /** Replace the project list for the switcher. */
  setProjects: (projects: ProjectNavSummary[]) => void;
  /** Clear everything (e.g. on logout / project context teardown). */
  reset: () => void;

  // --- Reader helpers (called by the shell-hosted sidebar) ---
  /** Counts for a project, or `undefined` when nothing has been written. */
  getCounts: (projectId: string) => ProjectNavCounts | undefined;
}

export const useProjectNavDataStore = create<ProjectNavDataStore>()(
  devtools(
    (set, get) => ({
      countsByProject: {},
      projects: [],

      setCounts: (projectId, counts) => {
        set(
          (state) => ({
            countsByProject: {
              ...state.countsByProject,
              [projectId]: { ...state.countsByProject[projectId], ...counts },
            },
          }),
          false,
          'projectNavData/setCounts'
        );
      },

      setProjects: (projects) => {
        set({ projects }, false, 'projectNavData/setProjects');
      },

      reset: () => {
        set({ countsByProject: {}, projects: [] }, false, 'projectNavData/reset');
      },

      getCounts: (projectId) => get().countsByProject[projectId],
    }),
    { name: 'Helios Project Nav-Data Store' }
  )
);
